if (!customElements.get('yt-product')) {
  // Copies the shop's own money format from a rendered sample (123456 | money).
  const moneyFormatter = (sample) => {
    const withCents = sample.match(/1([^\d]?)234([^\d])56/);
    const noCents = withCents ? null : sample.match(/1([^\d]?)235/);
    const match = withCents || noCents;
    if (!match) return (cents) => (cents / 100).toFixed(2);
    const prefix = sample.slice(0, match.index);
    const suffix = sample.slice(match.index + match[0].length);
    return (cents) => {
      const whole = withCents ? Math.floor(cents / 100) : Math.round(cents / 100);
      const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, match[1]);
      const fraction = withCents ? `${match[2]}${String(cents % 100).padStart(2, '0')}` : '';
      return `${prefix}${grouped}${fraction}${suffix}`;
    };
  };

  class YtProduct extends HTMLElement {
    connectedCallback() {
      const data = JSON.parse(this.querySelector('[data-yt-product-json]').textContent);
      this.variants = data.variants;
      this.strings = data.strings;
      this.money = moneyFormatter(this.dataset.moneySample || '');
      this.input = this.querySelector('[data-yt-variant-input]');
      this.variant = this.variants.find((v) => v.id === Number(this.input?.value)) || this.variants[0];

      this.setupGallery();
      this.setupZoom();
      this.setupOptions();
      this.setupQuantity();
      this.setupDock();
      this.setupReviews();
      this.setupStory();
      this.renderEta();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.revealer?.disconnect();
    }

    // ---------- gallery: one 3:4 photo at a time (swipe, arrows, thumbnail rail) ----------
    setupGallery() {
      this.track = this.querySelector('[data-yt-track]');
      if (!this.track) return;
      this.count = this.querySelector('[data-yt-count]');
      this.thumbs = [...this.querySelectorAll('[data-yt-thumb]')];
      this.at = 0;

      let pending;
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(pending);
          pending = requestAnimationFrame(() => this.markSlide());
        },
        { passive: true }
      );
      this.querySelectorAll('[data-yt-step]').forEach((button) =>
        button.addEventListener('click', () => this.goSlide(this.at + Number(button.dataset.ytStep)))
      );
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goSlide(Number(thumb.dataset.ytThumb))));
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.goSlide(this.at + (event.key === 'ArrowLeft' ? -1 : 1));
        }
      });
    }

    slides() {
      return [...this.track.children];
    }

    goSlide(index, smooth = true) {
      const slides = this.slides();
      if (!slides.length) return;
      const i = (index + slides.length) % slides.length;
      this.track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      this.setSlide(i);
    }

    markSlide() {
      const i = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
      if (i !== this.at) this.setSlide(i);
    }

    setSlide(i) {
      this.at = i;
      if (this.count) this.count.textContent = String(i + 1).padStart(2, '0');
      this.thumbs.forEach((thumb, n) => {
        if (n === i) {
          thumb.setAttribute('aria-current', 'true');
          const rail = thumb.closest('[data-yt-rail]');
          const li = thumb.parentElement;
          if (rail && li) {
            const vertical = rail.scrollHeight > rail.clientHeight + 2;
            rail.scrollTo(
              vertical
                ? { top: li.offsetTop - rail.clientHeight / 2 + li.offsetHeight / 2, behavior: 'smooth' }
                : { left: li.offsetLeft - rail.clientWidth / 2 + li.offsetWidth / 2, behavior: 'smooth' }
            );
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    // The chosen variant's photo slides into view.
    showMedia(mediaId) {
      if (!mediaId || !this.track) return;
      const index = this.slides().findIndex((s) => Number(s.dataset.mediaId) === mediaId);
      if (index >= 0) this.goSlide(index);
    }

    // ---------- full screen photos ----------
    setupZoom() {
      this.zoom = this.querySelector('[data-yt-zoombox]');
      if (!this.zoom || !this.track) return;
      this.zoomImg = this.zoom.querySelector('[data-yt-zoom-img]');
      this.zoomIndex = this.zoom.querySelector('[data-yt-zoom-index]');

      this.track.addEventListener('click', (event) => {
        const button = event.target.closest('[data-yt-zoom]');
        if (!button) return;
        this.openZoom(Math.max(0, this.zoomable().indexOf(button.closest('[data-full]'))));
      });
      this.zoom.querySelector('[data-yt-zoom-close]')?.addEventListener('click', () => this.zoom.close());
      this.zoom.querySelectorAll('[data-yt-zoom-step]').forEach((button) =>
        button.addEventListener('click', () => this.stepZoom(Number(button.dataset.ytZoomStep)))
      );
      this.zoom.addEventListener('click', (event) => {
        if (event.target === this.zoom) this.zoom.close();
      });
      this.zoom.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.stepZoom(-1);
        if (event.key === 'ArrowRight') this.stepZoom(1);
      });
      let startX = null;
      this.zoom.addEventListener('touchstart', (event) => (startX = event.touches[0].clientX), { passive: true });
      this.zoom.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.stepZoom(dx < 0 ? 1 : -1);
        startX = null;
      });
    }

    zoomable() {
      return this.slides().filter((s) => s.querySelector('img') && s.dataset.full);
    }

    openZoom(index) {
      this.zoomAt = index;
      this.renderZoom();
      if (!this.zoom.open) this.zoom.showModal();
    }

    stepZoom(step) {
      const count = this.zoomable().length;
      this.zoomAt = (this.zoomAt + step + count) % count;
      this.renderZoom();
    }

    renderZoom() {
      const slide = this.zoomable()[this.zoomAt];
      if (!slide) return;
      this.zoomImg.src = slide.dataset.full;
      this.zoomImg.alt = slide.querySelector('img')?.alt || '';
      if (this.zoomIndex) this.zoomIndex.textContent = this.zoomAt + 1;
    }

    // ---------- story: reveal on scroll, link to the full text ----------
    setupStory() {
      this.querySelectorAll('[data-yt-reveal]').forEach((block) =>
        block.querySelectorAll('.yt-chapter, .yt-tale__list li, .yt-readout__cell').forEach((item, i) => item.style.setProperty('--i', i))
      );
      this.revealer = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            this.revealer.unobserve(entry.target);
          }),
        { rootMargin: '0px 0px -12% 0px' }
      );
      this.querySelectorAll('[data-yt-reveal]').forEach((block) => this.revealer.observe(block));

      const pickerLink = this.querySelector('[data-yt-picker-link]');
      if (pickerLink && !document.getElementById('yt-shop')) pickerLink.href = `${window.Shopify?.routes?.root || '/'}#yt-shop`;

      this.querySelectorAll('[data-yt-open]').forEach((button) =>
        button.addEventListener('click', () => {
          const target = document.getElementById(button.dataset.ytOpen);
          if (!target) return;
          target.open = true;
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
      );
    }

    // ---------- variants ----------
    setupOptions() {
      this.fieldsets = [...this.querySelectorAll('[data-yt-option]')];
      if (!this.fieldsets.length) return;
      this.addEventListener('change', (event) => {
        if (!event.target.closest('[data-yt-option]')) return;
        const options = this.fieldsets.map((set) => set.querySelector('input:checked')?.value);
        this.fieldsets.forEach((set, i) => {
          const label = set.querySelector('[data-yt-option-value]');
          if (label) label.textContent = options[i] || '';
        });
        const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
        this.update(variant);
      });
      this.markAvailability();
    }

    // Values that have no available variant with the other current choices get a struck style.
    markAvailability() {
      const chosen = this.fieldsets.map((set) => set.querySelector('input:checked')?.value);
      this.fieldsets.forEach((set, index) => {
        set.querySelectorAll('input').forEach((input) => {
          const options = [...chosen];
          options[index] = input.value;
          const match = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
          input.parentElement.classList.toggle('is-out', !match || !match.available);
        });
      });
    }

    update(variant) {
      this.variant = variant;
      const available = Boolean(variant?.available);
      const label = !variant ? this.strings.unavailable : available ? this.strings.add : this.strings.soldOut;

      if (variant && this.input) {
        this.input.value = variant.id;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      const price = this.querySelector('[data-yt-price]');
      const compare = this.querySelector('[data-yt-compare]');
      const save = this.querySelector('[data-yt-save]');
      if (variant && price) {
        price.textContent = this.money(variant.price);
        const onSale = variant.compare > variant.price;
        if (compare) {
          compare.hidden = !onSale;
          compare.textContent = onSale ? this.money(variant.compare) : '';
        }
        if (save) {
          save.hidden = !onSale;
          save.textContent = onSale ? this.strings.save.replace('[amount]', this.money(variant.compare - variant.price)) : '';
        }
      }

      [this.querySelector('[data-yt-add]'), this.dockAdd].filter(Boolean).forEach((button) => {
        button.disabled = !available;
        const span = button.querySelector('span:not(.loading__spinner)');
        (span || button).textContent = label;
      });
      const dockPrice = this.querySelector('[data-yt-dock-price]');
      if (dockPrice && variant) dockPrice.textContent = this.money(variant.price);
      const express = this.querySelector('[data-yt-express]');
      if (express) express.hidden = !available;

      if (variant) this.renderStock(variant);
      this.toggle('[data-yt-eta]', available);
      this.toggle('[data-yt-made-note]', !available);
      const hand = this.querySelector('[data-yt-step-hand]');
      const check = this.querySelector('[data-yt-step-check]');
      hand?.classList.toggle('is-done', available);
      hand?.classList.toggle('is-now', !available);
      check?.classList.toggle('is-done', available);

      this.markAvailability();
      if (variant) this.showMedia(variant.media);
    }

    // Tracked stock: more than 10 green, 6–10 orange, 1–5 red; untracked or oversold shows plain "in stock".
    renderStock(variant) {
      const box = this.querySelector('[data-yt-stock]');
      if (!box) return;
      const s = this.strings;
      const stock = variant.available ? variant.stock || 0 : 0;
      const level = !variant.available ? 'out' : stock > 10 ? 'many' : stock > 5 ? 'low' : stock > 0 ? 'last' : 'plain';
      const fill = (text) => (text || '').replace('[count]', stock);
      const texts = {
        out: [s.outStock, ''],
        plain: [s.inStock, ''],
        many: [s.inStock, s.stockManyNote],
        low: [fill(s.stockLow), s.stockLowNote],
        last: [fill(stock === 1 ? s.stockLastOne : stock < 5 ? s.stockLastFew : s.stockLastMany), s.stockLastNote],
      };
      const counted = level === 'many' || level === 'low' || level === 'last';
      box.className = `yt-stock yt-stock--${level}`;
      box.querySelector('[data-yt-stock-text]').textContent = texts[level][0];
      const count = box.querySelector('[data-yt-stock-count]');
      const bar = box.querySelector('[data-yt-stock-bar]');
      const note = box.querySelector('[data-yt-stock-note]');
      count.hidden = bar.hidden = note.hidden = !counted;
      count.textContent = level === 'many' ? s.stockCountMany : fill(s.stockCount);
      bar.firstElementChild.style.setProperty('--w', `${level === 'many' ? 100 : Math.floor((stock * 100) / 15)}%`);
      note.innerHTML = texts[level][1];
    }

    toggle(selector, show) {
      const el = this.querySelector(selector);
      if (el) el.hidden = !show;
    }

    renderEta() {
      const eta = this.querySelector('[data-yt-eta]');
      if (eta && window.YtEta) window.YtEta.fill(eta);
    }

    // ---------- quantity + mobile dock ----------
    setupQuantity() {
      const field = this.querySelector('.yt-qty input');
      if (!field) return;
      this.querySelectorAll('[data-yt-qty]').forEach((button) =>
        button.addEventListener('click', () => {
          field.value = Math.max(1, (parseInt(field.value, 10) || 1) + Number(button.dataset.ytQty));
        })
      );
    }

    setupDock() {
      this.dock = this.querySelector('[data-yt-dock]');
      this.dockAdd = this.querySelector('[data-yt-dock-add]');
      const add = this.querySelector('[data-yt-add]');
      if (!this.dock || !add) return;
      this.dockAdd.addEventListener('click', () => add.closest('form')?.requestSubmit(add));
      this.observer = new IntersectionObserver(([entry]) => {
        this.dock.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      });
      this.observer.observe(add);
    }

    // ---------- reviews card: same carousel as the map shop; "all" opens Judge.me's full list ----------
    setupReviews() {
      const el = this.querySelector('.yt-pdx__reviews [data-yt-reviews]');
      if (!el) return;
      const all = el.querySelector('[data-yt-reviews-all]');
      const widget = el.querySelector('.yt-reviews__jdgm');
      all?.addEventListener('click', (event) => {
        const more = widget?.querySelector('.jdgm-row-readmore .jdgm-btn, .jdgm-rev-widg__reviews ~ .jdgm-paginate .jdgm-btn');
        event.preventDefault();
        if (more) {
          more.click();
        } else if (widget) {
          widget.hidden = false;
          all.hidden = true;
          widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      const track = el.querySelector('[data-yt-reviews-track]');
      const dots = [...el.querySelectorAll('[data-yt-reviews-dots] button')];
      if (!track || !dots.length) return;
      const cards = [...track.children];
      const current = () => Math.round(track.scrollLeft / Math.max(1, cards[0].offsetWidth));
      const go = (index) => {
        const n = (index + cards.length) % cards.length;
        track.scrollTo({ left: cards[n].offsetLeft - cards[0].offsetLeft, behavior: 'smooth' });
      };
      dots.forEach((dot, n) => dot.addEventListener('click', () => go(n)));
      el.querySelectorAll('[data-yt-reviews-step]').forEach((button) =>
        button.addEventListener('click', () => go(current() + Number(button.dataset.ytReviewsStep)))
      );
      let frame = 0;
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const n = current();
            dots.forEach((dot, i) => (i === n ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
          });
        },
        { passive: true }
      );
    }
  }

  customElements.define('yt-product', YtProduct);
}
